import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import AdminAuditLog from '../components/AdminAuditLog';

interface AdminInvitation {
  invitation_id: string;
  target_email: string;
  inviter_email: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
  expires_at: string;
  is_expired: boolean;
  accepted_at?: string;
  rejected_at?: string;
}

interface InvitationsResponse {
  invitations: AdminInvitation[];
  total_count: number;
  status_counts: {
    pending: number;
    accepted: number;
    rejected: number;
    expired: number;
  };
}

const AdminInvitationsPage: React.FC = () => {
  const { userRole } = useAuth();
  const [invitations, setInvitations] = useState<AdminInvitation[]>([]);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    accepted: 0,
    rejected: 0,
    expired: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedInvitation, setSelectedInvitation] = useState<AdminInvitation | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    if (userRole === 'admin') {
      loadInvitations();
    }
  }, [userRole]);

  const loadInvitations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/v1/admin/invitations');
      const data: InvitationsResponse = response.data.data;

      setInvitations(data.invitations);
      setStatusCounts(data.status_counts);
    } catch (err: any) {
      console.error('Failed to load invitations:', err);
      setError(err.response?.data?.error?.message || 'Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (invitation: AdminInvitation) => {
    setSelectedInvitation(invitation);
    setShowDetailsModal(true);
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteEmail.trim()) {
      setError('Email address is required');
      return;
    }

    try {
      setIsInviting(true);
      setError(null);

      await apiClient.post('/v1/admin/invitations', {
        email: inviteEmail.trim(),
        message: inviteMessage.trim() || undefined,
      });

      // Reset form and close modal
      setInviteEmail('');
      setInviteMessage('');
      setShowInviteModal(false);

      // Reload invitations
      await loadInvitations();
    } catch (err: any) {
      console.error('Failed to send invitation:', err);
      setError(err.response?.data?.error?.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const getStatusBadgeClass = (invitation: AdminInvitation) => {
    if (invitation.is_expired) return 'bg-secondary';
    switch (invitation.status) {
      case 'pending': return 'bg-warning text-dark';
      case 'accepted': return 'bg-success';
      case 'rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const getStatusText = (invitation: AdminInvitation) => {
    if (invitation.is_expired) return 'Expired';
    return invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1);
  };

  const getStatusIcon = (invitation: AdminInvitation) => {
    if (invitation.is_expired) return 'fas fa-clock';
    switch (invitation.status) {
      case 'pending': return 'fas fa-hourglass-half';
      case 'accepted': return 'fas fa-check-circle';
      case 'rejected': return 'fas fa-times-circle';
      default: return 'fas fa-question-circle';
    }
  };

  const filteredInvitations = invitations.filter(invitation => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'expired') return invitation.is_expired;
    return invitation.status === filterStatus && !invitation.is_expired;
  });

  if (userRole !== 'admin') {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Access denied. Admin privileges required.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>
                <i className="fas fa-user-plus me-2 text-primary"></i>
                Admin Invitations
              </h1>
              <p className="text-muted mb-0">
                Manage administrator role invitations
              </p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => setShowInviteModal(true)}
              >
                <i className="fas fa-plus me-2"></i>
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {error}
              <button
                type="button"
                className="btn-close float-end"
                onClick={() => setError(null)}
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Status Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3 mb-3">
          <div className="card bg-warning text-dark">
            <div className="card-body text-center">
              <i className="fas fa-hourglass-half fa-2x mb-2"></i>
              <h4 className="mb-1">{statusCounts.pending}</h4>
              <small>Pending</small>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card bg-success text-white">
            <div className="card-body text-center">
              <i className="fas fa-check-circle fa-2x mb-2"></i>
              <h4 className="mb-1">{statusCounts.accepted}</h4>
              <small>Accepted</small>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card bg-danger text-white">
            <div className="card-body text-center">
              <i className="fas fa-times-circle fa-2x mb-2"></i>
              <h4 className="mb-1">{statusCounts.rejected}</h4>
              <small>Rejected</small>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div className="card bg-secondary text-white">
            <div className="card-body text-center">
              <i className="fas fa-clock fa-2x mb-2"></i>
              <h4 className="mb-1">{statusCounts.expired}</h4>
              <small>Expired</small>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                Invitation History
                <span className="badge bg-primary ms-2">{filteredInvitations.length}</span>
              </h5>
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  <option value="all">All Invitations</option>
                  <option value="pending">Pending Only</option>
                  <option value="accepted">Accepted Only</option>
                  <option value="rejected">Rejected Only</option>
                  <option value="expired">Expired Only</option>
                </select>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={loadInvitations}
                  disabled={isLoading}
                >
                  <i className="fas fa-sync-alt me-1"></i>
                  Refresh
                </button>
              </div>
            </div>
            <div className="card-body">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading invitations...</p>
                </div>
              ) : filteredInvitations.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-envelope fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No invitations found</h5>
                  <p className="text-muted">
                    {filterStatus === 'all'
                      ? 'No admin invitations have been sent yet.'
                      : `No ${filterStatus} invitations found.`
                    }
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Recipient</th>
                        <th>Invited By</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Expires</th>
                        <th>Action Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvitations.map((invitation) => (
                        <tr key={invitation.invitation_id}>
                          <td>
                            <div>
                              <strong>{invitation.target_email}</strong>
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">{invitation.inviter_email}</small>
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(invitation)}`}>
                              <i className={`${getStatusIcon(invitation)} me-1`}></i>
                              {getStatusText(invitation)}
                            </span>
                          </td>
                          <td>
                            <small>{new Date(invitation.created_at).toLocaleDateString()}</small>
                          </td>
                          <td>
                            <small className={invitation.is_expired ? 'text-danger' : 'text-muted'}>
                              {new Date(invitation.expires_at).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            {invitation.accepted_at && (
                              <small className="text-success">
                                Accepted {new Date(invitation.accepted_at).toLocaleDateString()}
                              </small>
                            )}
                            {invitation.rejected_at && (
                              <small className="text-danger">
                                Rejected {new Date(invitation.rejected_at).toLocaleDateString()}
                              </small>
                            )}
                            {!invitation.accepted_at && !invitation.rejected_at && (
                              <small className="text-muted">-</small>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleViewDetails(invitation)}
                              title="View invitation details"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Audit Log */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="fas fa-history me-2"></i>
                Recent Admin Actions
              </h6>
            </div>
            <div className="card-body">
              <AdminAuditLog maxEntries={10} showTitle={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Send Invitation Modal */}
      {showInviteModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSendInvitation}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-user-plus me-2"></i>
                    Send Admin Invitation
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowInviteModal(false)}
                    disabled={isInviting}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="inviteEmail" className="form-label">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="inviteEmail"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                      disabled={isInviting}
                    />
                    <div className="form-text">
                      The user must already have an account in the system.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="inviteMessage" className="form-label">
                      Personal Message (Optional)
                    </label>
                    <textarea
                      className="form-control"
                      id="inviteMessage"
                      rows={3}
                      value={inviteMessage}
                      onChange={(e) => setInviteMessage(e.target.value)}
                      placeholder="Add a personal message to the invitation..."
                      maxLength={500}
                      disabled={isInviting}
                    />
                    <div className="form-text">
                      {inviteMessage.length}/500 characters
                    </div>
                  </div>
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Note:</strong> The invitation will expire in 7 days. The user will receive an email notification (when email system is configured).
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowInviteModal(false)}
                    disabled={isInviting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isInviting || !inviteEmail.trim()}
                  >
                    {isInviting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Details Modal */}
      {showDetailsModal && selectedInvitation && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-info-circle me-2"></i>
                  Invitation Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-muted">Invitation Information</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Recipient:</strong></td>
                          <td>{selectedInvitation.target_email}</td>
                        </tr>
                        <tr>
                          <td><strong>Invited By:</strong></td>
                          <td>{selectedInvitation.inviter_email}</td>
                        </tr>
                        <tr>
                          <td><strong>Status:</strong></td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(selectedInvitation)}`}>
                              <i className={`${getStatusIcon(selectedInvitation)} me-1`}></i>
                              {getStatusText(selectedInvitation)}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Invitation ID:</strong></td>
                          <td>
                            <code className="small">{selectedInvitation.invitation_id}</code>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-muted">Timeline</h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td><strong>Created:</strong></td>
                          <td>{new Date(selectedInvitation.created_at).toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td><strong>Expires:</strong></td>
                          <td className={selectedInvitation.is_expired ? 'text-danger' : ''}>
                            {new Date(selectedInvitation.expires_at).toLocaleString()}
                            {selectedInvitation.is_expired && (
                              <span className="badge bg-danger ms-2">EXPIRED</span>
                            )}
                          </td>
                        </tr>
                        {selectedInvitation.accepted_at && (
                          <tr>
                            <td><strong>Accepted:</strong></td>
                            <td className="text-success">
                              {new Date(selectedInvitation.accepted_at).toLocaleString()}
                            </td>
                          </tr>
                        )}
                        {selectedInvitation.rejected_at && (
                          <tr>
                            <td><strong>Rejected:</strong></td>
                            <td className="text-danger">
                              {new Date(selectedInvitation.rejected_at).toLocaleString()}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {selectedInvitation.status === 'pending' && !selectedInvitation.is_expired && (
                  <div className="mt-4">
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Pending Invitation:</strong> This invitation is waiting for the user to accept or reject it.
                      The user will receive an email notification when the email system is configured.
                    </div>
                  </div>
                )}

                {selectedInvitation.is_expired && (
                  <div className="mt-4">
                    <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Expired Invitation:</strong> This invitation has expired and can no longer be accepted.
                      You may need to send a new invitation if the user still needs admin access.
                    </div>
                  </div>
                )}

                {selectedInvitation.status === 'accepted' && (
                  <div className="mt-4">
                    <div className="alert alert-success">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>Invitation Accepted:</strong> The user has successfully accepted the admin role invitation
                      and now has administrator privileges.
                    </div>
                  </div>
                )}

                {selectedInvitation.status === 'rejected' && (
                  <div className="mt-4">
                    <div className="alert alert-danger">
                      <i className="fas fa-times-circle me-2"></i>
                      <strong>Invitation Rejected:</strong> The user has declined the admin role invitation.
                      You may send a new invitation if needed.
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
                {selectedInvitation.status === 'pending' && !selectedInvitation.is_expired && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowDetailsModal(false);
                      // Could add functionality to resend invitation here
                    }}
                  >
                    <i className="fas fa-paper-plane me-2"></i>
                    Resend Invitation
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvitationsPage;
